import {getCurrentCountry, getCurrentLanguage} from "../../services/i18n";

export interface IOptionItem {
    value: string;
    label: string;
    selected?: boolean;
}

/** Format: dc-ar (dc-<language-code (ISO 639-1)>) */
export type TCountryId = `dc-${string}`;
export type TLanguageId = `${string}_${string}`;

export interface ILanguageEntry {
    /** Format: en_GB (<language-code (ISO 639-1)>_<country-code (ISO 3166-1 alpha-2)> ) */
    id: string;
    /** Human-readable language self-name ("English" for English, "français" for French, etc.) */
    title: string;
    /** URL for the page in the language. Ends with `/languagecode/countrycode/`. */
    url: string;
}

export interface ICountryEntry {
    id: TCountryId;
    /** Human-readable country name (in language dependent from the request url) */
    title: string;
    /** Can be empty */
    languages: Array<ILanguageEntry>;
    /** URL for the page in the country's default language. Ends with `/languagecode/countrycode/`. */
    url: string;
}

export interface IRegionEntry {
    countries: Array<TCountryId>;
    /** Human-readable region title (in language dependent from the request url) */
    title: string;
}

export interface ISectionEntry<TSectionId extends 'regions' | 'countries', TStructure> {
    sectionId: TSectionId;
    structure: TStructure;
    visibleToRoles: Array<unknown>;
}

export interface IApiResponse {
    customDefinedDataStructureSections: [
        ISectionEntry<'regions', Record<string, IRegionEntry>>,
        ISectionEntry<'countries', { countries: Array<ICountryEntry> }>,
    ];
}

function getCountriesResponse(apiResponse: IApiResponse): ISectionEntry<"countries", {countries: ICountryEntry[]}> | undefined {
    return apiResponse.customDefinedDataStructureSections
        .find(section => section.sectionId == 'countries') as ISectionEntry<'countries', { countries: ICountryEntry[] }> | undefined;
}

export function getCountryList(apiResponse: IApiResponse): IOptionItem[] {
    const countriesResponse = getCountriesResponse(apiResponse);

    if (!countriesResponse) {
        console.error('Error reading language chooser API response!');
        return [];
    }

    const currentCountry = getCurrentCountryId();
    return countriesResponse.structure.countries.map(entry => ({
        label: entry.title,
        selected: entry.id == currentCountry,
        value: entry.url,
    }));
}

export function getCurrentCountryId(): TCountryId {
    // the second level of the URL path contains the correct region
    // de is fallback for localhost, should never execute on prod
    return `dc-${getCurrentCountry()}`;
}

export function getCurrentLanguageId(langList: Array<TLanguageId>): TLanguageId {
    const currentLang = getCurrentLanguage();
    return langList.find(lang => lang.startsWith(currentLang)) ?? langList[0];
}

export function getLanguageList(apiResponse: IApiResponse): IOptionItem[] {
    const countriesResponse = getCountriesResponse(apiResponse);

    if (!countriesResponse) {
        console.error('Error reading language chooser API response!');
        return [];
    }

    const currentCountryId = getCurrentCountryId();
    const currentLanguages = countriesResponse.structure.countries.find(country => country.id == currentCountryId)?.languages ?? [];
    const languageIdList = currentLanguages.map(language => language.id) as TLanguageId[];
    const currentLanguageId = getCurrentLanguageId(languageIdList);
    const altUrls = Array.from(document.head.querySelectorAll<HTMLLinkElement>('link[rel="alternate"]'))
        .map(altEle => [altEle.hreflang, altEle.href]);

    return currentLanguages
        .map(entry => {
            const isCurrentLanguage = entry.id == currentLanguageId;

            if (altUrls.length > 0) {
                const alternateUrl = altUrls.find(alt => alt[0].startsWith(entry.id.slice(0, 2)))?.[1];

                // only show entry if an alternate link exists or it's the current language
                return alternateUrl || isCurrentLanguage
                    ? {
                        label: entry.title,
                        selected: isCurrentLanguage,
                        value: alternateUrl ?? location.href,
                    }
                    : undefined;
            } else {
                // just show everything from the API
                return {
                    label: entry.title,
                    selected: isCurrentLanguage,
                    value: isCurrentLanguage ? location.href : entry.url,
                }
            }
        })
        .filter(Boolean);
}
